import { FC, useEffect, useState, createContext, useContext } from 'react';
import { Box, AppBar, Toolbar, Typography, Button, Container, Tabs, Tab, CircularProgress, IconButton, Tooltip } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import SchoolIcon from '@mui/icons-material/School';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useThemeMode } from './contexts/ThemeContext';
import { initKeycloak, logout, getUsername } from './services/keycloak';
import { BrowserView } from './components/BrowserView';
import { DebugPanel } from './components/DebugPanel';
import { BasePrefixSelector } from './components/BasePrefixSelector';
import { MoodleImportDialog } from './components/MoodleImportDialog';
import { SettingsDialog } from './components/SettingsDialog';
import { JobsProvider } from './contexts/JobsContext';
import { JobsButton } from './components/JobsButton';
import { JobsPanel } from './components/JobsPanel';
import { JobNotifications } from './components/JobNotifications';
import { WizardsSection } from './components/WizardsSection';
import { RangeOSContentManager } from './components/RangeOSContentManager';
import { MoodleContentManager } from './components/MoodleContentManager';
import { GitLabManager } from './components/GitLabManager';
import { TerraformBuilder } from './components/TerraformBuilder';
import GitLabIcon from '@mui/icons-material/Inventory2';
import BuildIcon from '@mui/icons-material/Build';

// Context for managing Moodle import dialogs
interface MoodleImportTask {
  id: string;
  targetFolder: 'courses' | 'competency_frameworks' | 'learning_path_templates' | 'cmi5_activities';
  targetEnvironment: string;
  onComplete: () => void;
}

interface MoodleImportContextType {
  openMoodleImport: (task: Omit<MoodleImportTask, 'id'>) => void;
}

const MoodleImportContext = createContext<MoodleImportContextType | null>(null);

export const useMoodleImport = () => {
  const context = useContext(MoodleImportContext);
  if (!context) {
    throw new Error('useMoodleImport must be used within MoodleImportProvider');
  }
  return context;
};

const App: FC = () => {
  const { mode, toggleTheme } = useThemeMode();
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // View modes: 'browse' (tools tabs), 'wizards', 'config'
  const [activeTab, setActiveTab] = useState(0); // Tools tabs: 0=Browser, 1=Moodle, 2=RangeOS, 3=GitLab, 4=Terraform, 5=Debug
  const [activeMode, setActiveMode] = useState<'browse' | 'wizards' | 'config'>('wizards');
  const [username, setUsername] = useState<string>('');
  const [showDebug, setShowDebug] = useState(false);
  const [contentKey, setContentKey] = useState(0); // Force re-render when prefix changes
  const [currentEnvironment, setCurrentEnvironment] = useState<string>('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<number>(0);
  const [moodleImportTasks, setMoodleImportTasks] = useState<MoodleImportTask[]>([]);
  const [environmentSelectorKey, setEnvironmentSelectorKey] = useState(0); // Force reload of environments

  const openMoodleImport = (task: Omit<MoodleImportTask, 'id'>) => {
    const newTask: MoodleImportTask = {
      ...task,
      id: `import-${Date.now()}-${Math.random()}`,
    };
    setMoodleImportTasks(prev => [...prev, newTask]);
  };

  const closeMoodleImport = (taskId: string) => {
    setMoodleImportTasks(prev => prev.filter(t => t.id !== taskId));
  };

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const kc = await initKeycloak();
        if (isMounted) {
          setUsername(getUsername());
          // Small delay to ensure token is fully set up
          await new Promise(resolve => setTimeout(resolve, 100));
          setInitialized(true);
        }
      } catch (err) {
        console.error('Failed to initialize Keycloak:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Initialization failed');
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Listen for Ctrl+Shift+D to toggle debug mode
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDebug(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    // Switch to debug tab when debug mode is enabled
    if (showDebug) {
      setActiveTab(5); // Debug tab is at position 5 (Browser=0, Moodle=1, RangeOS=2, GitLab=3, Terraform=4, Debug=5)
    }
  }, [showDebug]);

  useEffect(() => {
    // Check if we're coming back from Keycloak OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const isKeycloakCallback = urlParams.get('keycloak_callback');
    const oauthRedirectFlag = sessionStorage.getItem('keycloak_oauth_redirect');

    if (isKeycloakCallback === '1' && oauthRedirectFlag === 'true') {
      console.log('Detected Keycloak OAuth callback, opening settings dialog...');

      // Clear the flag
      sessionStorage.removeItem('keycloak_oauth_redirect');

      // Small delay to ensure components are mounted before opening dialog
      setTimeout(() => {
        // Open settings dialog to Integrations tab (tab 3)
        setSettingsInitialTab(3);
        setSettingsOpen(true);
        setActiveMode('config');
      }, 100);
    }
  }, []);

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4" color="error" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 4 }}>
            {error}
          </Typography>
          {error.includes('role') && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              This application requires the <strong>content-administrator</strong> role.
              Please contact your administrator for access.
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={() => {
              // Clear the role check failed flag so user can try again
              sessionStorage.clear();
              localStorage.clear();

              // Redirect to a logout URL that will clear Keycloak session
              // This constructs a logout URL manually since we don't have a Keycloak instance
              const keycloakUrl = 'https://keycloak.global.cloudcents.bylight.com/auth';
              const realm = 'cloudcents';
              const redirectUri = encodeURIComponent(window.location.origin);
              window.location.href = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout?redirect_uri=${redirectUri}`;
            }}
            sx={{ mt: 3 }}
          >
            Logout
          </Button>
        </Box>
      </Container>
    );
  }

  if (!initialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Authenticating...
        </Typography>
      </Box>
    );
  }

  return (
    <JobsProvider>
      <MoodleImportContext.Provider value={{ openMoodleImport }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <AppBar position="static">
            <Toolbar>
              <Typography
                variant="h6"
                sx={{
                  flexGrow: 1,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 }
                }}
                onClick={() => {
                  setActiveMode('wizards');
                }}
              >
                S3 Moodle Content Manager
              </Typography>
            <Button
              color="inherit"
              startIcon={<AutoFixHighIcon />}
              onClick={() => {
                setActiveMode('wizards');
              }}
              sx={{ mr: 1 }}
              variant={activeMode === 'wizards' ? 'outlined' : 'text'}
            >
              Wizards
            </Button>
            <Button
              color="inherit"
              startIcon={<FolderIcon />}
              onClick={() => {
                setActiveMode('browse');
                setActiveTab(0); // Browser tab
                // Refresh environment list when switching to Tools mode
                setEnvironmentSelectorKey(prev => prev + 1);
              }}
              sx={{ mr: 1 }}
              variant={activeMode === 'browse' ? 'outlined' : 'text'}
            >
              Tools
            </Button>
            <Button
              color="inherit"
              startIcon={<SettingsIcon />}
              onClick={() => {
                setActiveMode('config');
                setSettingsOpen(true);
              }}
              sx={{ mr: 1 }}
              variant={activeMode === 'config' ? 'outlined' : 'text'}
            >
              Config
            </Button>
            <JobsButton />
            <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton color="inherit" onClick={toggleTheme} sx={{ ml: 1 }}>
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ mr: 2, ml: 2 }}>
              {username}
            </Typography>
            <Button color="inherit" startIcon={<LogoutIcon />} onClick={() => logout()}>
              Logout
            </Button>
          </Toolbar>
        </AppBar>

      {/* Only show tabs when in Tools mode */}
      {activeMode === 'browse' && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue: number) => setActiveTab(newValue)}>
            <Tab label="S3 Browser" icon={<FolderIcon />} />
            <Tab label="Moodle" icon={<SchoolIcon />} />
            <Tab label="RangeOS" icon={<CloudQueueIcon />} />
            <Tab label="GitLab" icon={<GitLabIcon />} />
            <Tab label="Terraform" icon={<BuildIcon />} />
            {showDebug && <Tab label="Debug" icon={<SettingsIcon />} />}
          </Tabs>
        </Box>
      )}

      <Box sx={{ flex: 1 }}>
        {/* Wizards Mode */}
        {activeMode === 'wizards' && initialized && (
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <WizardsSection />
          </Container>
        )}

        {/* Browser tab - Tree view with all folders */}
        {activeMode === 'browse' && (
          <Box
            role="tabpanel"
            hidden={activeTab !== 0}
            sx={{ display: activeTab === 0 ? 'block' : 'none', height: '100%' }}
          >
            {activeTab === 0 && initialized && (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Environment selector for Browser tab */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      S3 Environment:
                    </Typography>
                    <BasePrefixSelector
                      key={environmentSelectorKey}
                      onPrefixChange={(prefix) => {
                        setCurrentEnvironment(prefix);
                        setContentKey(prev => prev + 1);
                      }}
                    />
                  </Box>
                </Box>
                {currentEnvironment && (
                  <BrowserView
                    key={contentKey}
                    currentEnvironment={currentEnvironment}
                    contentKey={contentKey}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Moodle Content Manager tab (Transfer Manager + CMI5 Browser) */}
        {activeMode === 'browse' && (
          <Box
            role="tabpanel"
            hidden={activeTab !== 1}
            sx={{ display: activeTab === 1 ? 'block' : 'none', height: '100%' }}
          >
            {activeTab === 1 && initialized && (
              <MoodleContentManager currentEnvironment={currentEnvironment} />
            )}
          </Box>
        )}

        {/* RangeOS Content Manager tab (Draft Scenarios + Ghost Traffic) */}
        {activeMode === 'browse' && (
          <Box
            role="tabpanel"
            hidden={activeTab !== 2}
            sx={{ display: activeTab === 2 ? 'block' : 'none', height: '100%' }}
          >
            {activeTab === 2 && initialized && (
              <RangeOSContentManager />
            )}
          </Box>
        )}

        {/* GitLab Manager tab */}
        {activeMode === 'browse' && (
          <Box
            role="tabpanel"
            hidden={activeTab !== 3}
            sx={{ display: activeTab === 3 ? 'block' : 'none', height: '100%' }}
          >
            {activeTab === 3 && initialized && (
              <GitLabManager />
            )}
          </Box>
        )}

        {/* Terraform Builder tab */}
        {activeMode === 'browse' && (
          <Box
            role="tabpanel"
            hidden={activeTab !== 4}
            sx={{ display: activeTab === 4 ? 'block' : 'none', height: '100%' }}
          >
            {activeTab === 4 && initialized && (
              <TerraformBuilder />
            )}
          </Box>
        )}

        {/* Debug panel tab - only show when debug mode is enabled and in Tools mode */}
        {activeMode === 'browse' && showDebug && (
          <Box
            role="tabpanel"
            hidden={activeTab !== 5}
            sx={{ display: activeTab === 5 ? 'block' : 'none', height: '100%' }}
          >
            {activeTab === 5 && <DebugPanel />}
          </Box>
        )}
      </Box>

      {/* Settings Dialog (for viewing/managing all config) */}
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialTab={settingsInitialTab}
      />

      {/* Moodle Import Dialogs - stacked and persistent across tab changes */}
      {moodleImportTasks.map((task) => (
        <MoodleImportDialog
          key={task.id}
          open={true}
          onClose={() => closeMoodleImport(task.id)}
          targetFolder={task.targetFolder}
          targetEnvironment={task.targetEnvironment}
          onImportComplete={() => {
            task.onComplete();
            closeMoodleImport(task.id);
          }}
        />
      ))}

      {/* Jobs Panel */}
      <JobsPanel />

      {/* Job Notifications */}
      <JobNotifications />
    </Box>
    </MoodleImportContext.Provider>
    </JobsProvider>
  );
};

export default App;
